import { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import type { Aviso, Espelho, RegistroDeAvisos } from "@/api/escala.service";
import type { User } from "@/api/user.service";
import { avisosPorRegra, quantidadeDeAvisos, type AvisosDaRegra } from "@/components/escala/espelho";

interface EscalaAvisosModalProps {
  open: boolean;
  avisos: Aviso[];
  // Para dizer de quem são as folgas seguidas e as semanas acima da jornada.
  colaboradores: Espelho["colaboradores"];
  // Para dar nome à carga sem ninguém trabalhando.
  cargas?: Espelho["cargas"];
  // As listas gravadas ao gerar, validar e aprovar.
  historico?: RegistroDeAvisos[];
  // Quem gerou, validou e aprovou, pelo nome.
  usuarios?: User[];
  onClose: () => void;
}

const ROTULO_ETAPA: Record<RegistroDeAvisos["etapa"], { etapa: string; com: string }> = {
  GERADA: { etapa: "Gerada", com: "com" },
  VALIDADA: { etapa: "Validada", com: "aceitando" },
  APROVADA: { etapa: "Aprovada", com: "aceitando" },
};

// "Validada aceitando 3 avisos", "Gerada sem avisos".
function resumoDoRegistro(etapa: RegistroDeAvisos["etapa"], quantos: number) {
  const { etapa: rotulo, com } = ROTULO_ETAPA[etapa];
  if (quantos === 0) return `${rotulo} sem avisos`;
  return `${rotulo} ${com} ${quantos} ${quantos === 1 ? "aviso" : "avisos"}`;
}

// "28/10/2026 10:00".
function dataHora(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

// No histórico, sem a dica de cada regra, para a lista de cada momento ficar curta.
function GruposDeAvisos({ grupos, semDica }: { grupos: AvisosDaRegra[]; semDica?: boolean }) {
  return (
    <Stack spacing={2.5}>
      {grupos.map((grupo) => (
        <Box key={grupo.regra}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {grupo.rotulo}
          </Typography>
          {grupo.dica && !semDica && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {grupo.dica}
            </Typography>
          )}
          <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
            {grupo.linhas.map((linha) => (
              <Typography component="li" variant="body2" key={linha}>
                {linha}
              </Typography>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

// Mesmo molde do modal de ocorrências, mas o que ele lista não impede a validação. Embaixo, o histórico:
// uma linha por momento gravado, que abre a lista daquele momento.
export function EscalaAvisosModal({ open, avisos, colaboradores, cargas, historico = [], usuarios = [], onClose }: EscalaAvisosModalProps) {
  const grupos = useMemo(() => avisosPorRegra(avisos, colaboradores, cargas), [avisos, colaboradores, cargas]);
  const nomes = useMemo(() => new Map(usuarios.map((u) => [u.id, u.name])), [usuarios]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>Avisos da escala</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {avisos.length > 0 && (
            <>
              <Alert severity="warning">
                Os avisos não impedem a validação, mas validar e aprovar pedem que você confirme estar ciente deles.
              </Alert>
              <GruposDeAvisos grupos={grupos} />
            </>
          )}

          {historico.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Histórico
              </Typography>
              {historico.map((r) => {
                const quantos = quantidadeDeAvisos(r.avisos, colaboradores);
                const quem = nomes.get(r.user_id);
                const resumo = (
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {resumoDoRegistro(r.etapa, quantos)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {quem ? `${dataHora(r.em)} — ${quem}` : dataHora(r.em)}
                    </Typography>
                  </Box>
                );
                return quantos === 0 ? (
                  <Box key={`${r.etapa}-${r.em}`} sx={{ px: 2, py: 1, mb: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
                    {resumo}
                  </Box>
                ) : (
                  <Accordion key={`${r.etapa}-${r.em}`} variant="outlined" disableGutters sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>{resumo}</AccordionSummary>
                    <AccordionDetails>
                      <GruposDeAvisos grupos={avisosPorRegra(r.avisos, colaboradores, cargas)} semDica />
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
